import { createUseStyles } from 'react-jss';
import { useScrollIntoView } from '../utils/useScrollIntoView';
import { useEffect, useRef, useState } from 'react';
import { IonButton } from '@ionic/react';
import { DeleteIcon } from '../theme/Icons';
import { ISO8601 } from '../support/ISO8601';
import dayjs from 'dayjs';

const useStyles = createUseStyles({
    collaboratorRowWrapper: {
        paddingLeft: 12,
        position: 'relative'
    },

    collaboratorRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    collaboratorCreated: {
        color: 'var(--ion-color-step-750)'
    },

    collaboratorEmail: {
        wordBreak: 'break-all',
        color: 'var(--ion-color-step-750)'
    },

    collaboratorEndCol: {
        minWidth: 60
    },

    collaboratorDeleteConfirm: {
        // this div needs explicit height, otherwise scrollIntoViewIfNeeded() doesn't work on desktop
        height: 48,
        paddingBottom: 8,
        display: 'flex',
        justifyContent: 'flex-end'
    },

    collaboratorDeleteClicked: {
        borderRadius: 8,
        paddingTop: 8,
        background: 'var(--ion-color-step-50)'
    },

    newIndicator: {
        position: 'absolute',
        width: 4,
        height: '100%',
        left: 2,
        backgroundColor: 'var(--ion-color-success)'
    }
});

interface ICollaboratorRow  {
    id: string;
    name: string;
    email: string;
    role?: string;
    createdAt?: ISO8601;
    type?: 'invite' | 'permission';
    canDelete?: boolean;
    'data-type'?: string;
    isNewlyAdded?: boolean;
    onDelete?: () => Promise<void>;
};

export const CollaboratorRow: React.FC<ICollaboratorRow> = (props) => {

    const {
        id,
        email,
        createdAt,
        canDelete = true,
        'data-type': dataType,
        isNewlyAdded = false,
        onDelete,
        role,
        type
    } = props;

    let { name } = props;

    const classes = useStyles();
    const scrollIntoView = useScrollIntoView();

    const deleteConfirmBtnRef = useRef<HTMLIonButtonElement>(null);

    // mostly shouldn't be possible (very old data)
    name = name || '(no name)';

    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        scrollIntoView(deleteConfirmBtnRef);
    }, [isDeleting, scrollIntoView]);

    return (
        <div
            data-type={dataType}
            data-type-v2="collaborator-row"
            data-email={email}
            data-id={id}
            className={`${classes.collaboratorRowWrapper} ${isDeleting ? classes.collaboratorDeleteClicked : ''}`}
        >
            {isNewlyAdded && (
                <div className={classes.newIndicator}></div>
            )}
            <div className={classes.collaboratorRow}>
                <div>
                    <div data-type="recipient-name">{name}</div>

                    {email && (
                        <div data-type="recipient-email" className={classes.collaboratorEmail}>{email}</div>
                    )}
                    {type && createdAt && (
                        <div className={classes.collaboratorCreated}>
                            {type === 'invite' && ('Invited ')}
                            {type === 'permission' && ('Joined ')}
                            {formatDate(createdAt)}
                        </div>
                    )}
                    {role && (
                        <div data-type="recipient-role">{role}</div>
                    )}
                </div>
                <div className={classes.collaboratorEndCol}>
                    <IonButton
                        shape="round"
                        fill="clear"
                        data-type="delete-button"
                        disabled={!canDelete || isDeleting || !onDelete}
                        style={{width: 48, height: 48}}
                        onClick={() => setIsDeleting(true)}
                    >
                        <DeleteIcon />
                    </IonButton>
                </div>
            </div>
            {onDelete && isDeleting && (
                <div className={classes.collaboratorDeleteConfirm}>
                    <IonButton
                        fill="clear"
                        color="danger"
                        ref={deleteConfirmBtnRef}
                        onClick={async () => {
                            // TODO: error handling
                            await onDelete();
                            setIsDeleting(false);
                        }}>Confirm Delete</IonButton>
                    <IonButton fill="clear" onClick={() => setIsDeleting(false)}>Cancel</IonButton>
                </div>
            )}
        </div>
    );
};

const formatDate = (date: string) => {
    const dayjsDate = dayjs(date);
    const isToday = dayjsDate.isToday();
    if (isToday){
        return 'Today';
    }
    else{
        return dayjsDate.format('M/D/YY');
    }
};
